import { useLazyQuery, useReactiveVar } from '@apollo/client';
import React, { useEffect } from 'react';
import { vars } from '../../reactive';
import { JSONparse, JSONstringify } from '../../utils';
import { GetAllBreeds } from '../../utils/queries';
import FormQuestion from './FormQuestion/FormQuestion';
import { getDefaultValue } from './FormQuestion/helpers';
import { FormQuestionsRender, Question, chronic_conditions, onQuestionSubmitType } from './FormQuestion/types';

const FormLazyQuestionsRender = React.memo(
  ({ questions: lazyQuestions, previousPage, onQuestionSubmit, formsAnswers, currentPage, nonLazyQuestionsLength }: FormQuestionsRender & { nonLazyQuestionsLength: number }) => {
    const [getBreeds, { data: { breedGet: newBreeds = [] } = {}, loading: breedsLoading, called: calledBreeds }] = useLazyQuery<{ breedGet: { id: string; name: string }[] }>(GetAllBreeds, {
      fetchPolicy: 'cache-and-network',
      variables: {
        // TODO: support other types by only fetching the breeds for the selected pet
        type: ['DOG', 'CAT', 'RABBIT', 'HORSE', 'DUCK', 'GUINEA_PIG']
      }
    });

    const hasBreeds = lazyQuestions.some(question => question.question.get_answers_from === 'BREED');

    const breeds = useReactiveVar(vars.breeds);

    useEffect(() => {
      if (hasBreeds && !newBreeds?.length && !calledBreeds && !vars.breeds().length) {
        getBreeds();
      }
    }, [hasBreeds]);

    useEffect(() => {
      if (newBreeds?.length) {
        vars.breeds(newBreeds);
      }
    }, [newBreeds]);

    const fixedQuestions = lazyQuestions.map(item => {
      const { question } = item;
      const { get_answers_from } = question;
      if (get_answers_from === 'BREED') {
        const breedOptions = breeds.map(breed => ({ id: breed.id, title: breed.name }));

        return { ...item, question: { ...question, type: 'DROPDOWN', AnswerOptions: breedOptions }, onSubmit: onQuestionSubmit, fixDefaultValue: (answer: string) => answer };
      }

      if (get_answers_from === 'CHRONIC_CONDITIONS') {
        const chronicConditionsOptions = chronic_conditions.map(condition => ({ id: condition, title: condition }));
        const onSubmit = (params: Parameters<onQuestionSubmitType>['0']) => {
          const answer = chronicConditionsOptions.reduce((acc, option) => {
            const { id } = option;
            const isSelected = params.answer.includes(id);
            return { ...acc, [id]: isSelected };
          }, {});

          const newParams = { ...params, answer: JSONstringify(answer) };

          onQuestionSubmit(newParams);
        };

        const fixDefaultValue = (stringifiedAnswer: string) => {
          const answer = JSONparse(stringifiedAnswer);
          if (!answer) {
            return [];
          }
          return Object.keys(answer).filter(condition => answer[condition]);
        };

        return { ...item, question: { ...question, type: 'CHECKBOX', AnswerOptions: chronicConditionsOptions }, onSubmit, fixDefaultValue };
      }

      if (get_answers_from === 'VET' || get_answers_from === 'INSURER') {
        const onSubmit = (params: Parameters<onQuestionSubmitType>['0']) => {
          const { answer } = params;
          const newParams = { ...params, answer: answer };
          onQuestionSubmit(newParams);
        };

        const fixDefaultValue = (stringifiedAnswer: string) => {
          const answer = JSONparse(stringifiedAnswer);
          if (!answer) {
            return {};
          }
          return answer;
        };

        return { ...item, question: { ...question, type: 'NAME_ADDRESS' }, onSubmit, fixDefaultValue };
      }

      return item;
    }) as (Question & { onSubmit: onQuestionSubmitType; fixDefaultValue: (answer: unknown) => unknown })[];

    const loading = breedsLoading;

    return (
      <>
        {fixedQuestions.map((item, index) => {
          const defaultAns = getDefaultValue(item, formsAnswers, lazyQuestions);
          const defaultValue = item.fixDefaultValue(defaultAns);

          return (
            currentPage === nonLazyQuestionsLength + index && (
              <FormQuestion
                key={item.question.id}
                item={item}
                registerAs={`question${index}`}
                onQuestionSubmit={item.onSubmit}
                previousPage={previousPage}
                loading={loading}
                defaultValue={defaultValue}
              />
            )
          );
        })}
      </>
    );
  }
);

export default FormLazyQuestionsRender;
